<template>
  <div v-if="titleColumn">
    <template v-if="!parentId && column.relation.requiresParentSave">
      <q-alert color="primary" type="warning">Must save before these can be created.</q-alert>
    </template>
    <template v-else>
      <div v-if="!isFilter">
        <!-- Default top view -->
        <div class="q-mb-md">
          <q-btn
            v-if="addButton"
            icon="add"
            @click="newFormModalOpen=!newFormModalOpen"
            size="sm"
            flat
            class="no-shadow"
            color="secondary"
            :label="'New ' + column.label | singular"
          ></q-btn>
          <q-btn
            v-show="editModeActive"
            icon="check"
            @click="editing = false"
            size="sm"
            flat
            class="on-right no-shadow"
            color="positive"
            v-if="value"
            :label="value | relationLabel(titleColumn, relationParentColumnMap[titleColumn.field])"
          ></q-btn>
        </div>
        <!-- Edit Mode -->
        <div v-if="editModeActive">
          <div class="flex">
            <q-btn
              :color="inputBgColor"
              text-color="primary"
              rounded
              :icon="showOptions ? 'keyboard arrow up' : 'keyboard arrow down'"
              @click="handleBrowseData"
              class="no-shadow inline-flex"
              :loading="loading"
              v-show="showBrowse"
            ></q-btn>
            <q-search
              v-model="term"
              v-if="loaded"
              placeholder
              hide-underline
              :color="inputBgColor"
              class="no-shadow inline-flex on-right"
              style="flex-grow: 1"
              inverted-light
              clearable
              v-show="showSearch"
            >
              <q-autocomplete
                :minCharacters="1"
                :max-results="25"
                max-height="400px"
                @selected="selected"
                @search="search"
              ></q-autocomplete>
            </q-search>
          </div>
          <!-- Explore Options -->
          <q-slide-transition>
            <div v-show="options && options.length && showOptions">
              <details-table
                :columns="relationColumns"
                :parent-column-map="relationParentColumnMap"
                :selected="value"
                :rows="options"
                :show-delete="false"
                :show-edit="allowEdit"
                @select="handleSelectClick"
                @remove="handleRemove"
                @edit="handleEditRequest"
              ></details-table>
              <div class="row q-mt-md">
                <div class="col-12">
                  <q-pagination
                    class="text-center justify-center"
                    :value="pagination.page"
                    color="secondary"
                    :min="1"
                    :max="maxPage"
                    :max-pages="15"
                    boundary-numbers
                    boundary-links
                    @change="handlePageChange"
                  />
                </div>
              </div>
            </div>
          </q-slide-transition>
          <q-slide-transition>
            <q-alert
              type="info"
              v-show="showOptions && !options.length && !loading"
            >No {{ column.label | plural }} found</q-alert>
          </q-slide-transition>
        </div>
        <!-- Viewing Mode -->
        <div v-if="!editModeActive" class="flat-box">
          <q-card flat>
            <q-card-main>
              <q-collapsible opened>
                <!-- header / title -->
                <div slot="header" class="full-width">
                  <span
                    class="q-title text-primary"
                  >{{ value | relationLabel(titleColumn, relationParentColumnMap[titleColumn.field]) }}</span>
                  <span v-if="subtitleColumn" class="q-caption text-grey-6">
                    <br>
                    {{ value | relationLabel(subtitleColumn, relationParentColumnMap[subtitleColumn.field]) }}
                  </span>
                </div>
                <!-- details -->
                <details-table
                  :columns="relationColumns"
                  :rows="[value]"
                  :selected="[value]"
                  :parent-column-map="relationParentColumnMap"
                  :show-edit="allowEdit"
                  @remove="handleRemove"
                  @edit="handleEditRequest"
                ></details-table>
              </q-collapsible>
            </q-card-main>
            <!-- actions -->
            <q-card-actions align="between">
              <q-btn @click="editing=true" class="no-shadow" color="secondary" flat v-if="showBrowse">
                <q-icon name="autorenew" class="on-left"></q-icon>
                select different {{ column.label | singular }}
              </q-btn>
              <q-btn
                v-if="realModel.id && allowEdit"
                color="positive"
                flat
                class="no-shadow"
                @click="handleEditButton(realModel.id)"
              >
                <span>edit/view record</span>
                <q-icon name="open in new" class="on-right"></q-icon>
              </q-btn>
            </q-card-actions>
          </q-card>
        </div>
        <!-- ADD NEW FORM -->
        <div v-if="addButton && template === 'form'">
          <div v-if="newFormModalOpen">
            <q-modal
              v-model="newFormModalOpen"
              content-classes="q-pa-lg"
              content-css="min-width: 70vw; max-width: 95vw"
              no-backdrop-dismiss
              no-esc-dismiss
            >
              <div class="fit">
                <q-btn
                  flat
                  icon="close"
                  class
                  style="top: 0; right: 0; position: relative; float: right"
                  @click="newFormModalOpen=false"
                ></q-btn>
                <div v-if="newFormModalOpen">
                  <h3
                    class="q-display-1 text-secondary capitalize no-margin"
                  >Add New {{ column.label | singular }}</h3>
                  <base-form
                    v-if="relationResource"
                    :resource="relationResource"
                    :parent-id="parseInt(parentId)"
                    :parent-resource="parentResource"
                    :parent-resource-column="column"
                    :skip-resource="resource"
                    @add="handleNew"
                    :is-modal="true"
                  >
                    <div slot="goback" class="full-width q-mt-md q-pb-lg">
                      <q-btn
                        icon="close"
                        label="Cancel / Close"
                        color="grey-6"
                        @click="newFormModalOpen=false"
                        flat
                      />
                    </div>
                  </base-form>
                </div>
              </div>
            </q-modal>
          </div>
        </div>
        <!-- EDIT FORM -->
        <div v-if="template === 'form'">
          <div v-if="editFormModalOpen">
            <q-modal
              v-model="editFormModalOpen"
              content-classes="q-pa-lg"
              content-css="min-width: 70vw; max-width: 95vw"
              no-backdrop-dismiss
              no-esc-dismiss
            >
              <div class="fit">
                <q-btn
                  flat
                  icon="close"
                  class
                  style="top: 0; right: 0; position: relative; float: right"
                  @click="editFormModalOpen=false"
                ></q-btn>
                <div v-if="editFormModalOpen">
                  <base-form
                    v-if="relationResource"
                    :resource="relationResource"
                    :parent-resource="parentResource"
                    :parent-resource-column="column"
                    :skip-resource="resource"
                    :callback="handleEdit"
                    :is-modal="true"
                    :id="value.id"
                    :compare="compare"
                    :compareId="compareId"
                  >
                    <div slot="goback" class="full-width q-mt-md q-pb-lg">
                      <q-btn
                        icon="close"
                        label="Cancel / Close"
                        color="grey-6"
                        @click="editFormModalOpen=false"
                        flat
                      />
                    </div>
                  </base-form>
                </div>
              </div>
            </q-modal>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
<script>
  import relationFieldMixin from '../../mixins/relationFields'
  import {relationLabel} from '../../plugins/filters'

  export default {
  name: 'RelationFieldOne',
  mixins: [relationFieldMixin],
  computed: {
    realModel () {
      if (this.value && this.column.field) {
        if (typeof this.value === 'object') {
          if (this.value.hasOwnProperty(this.column.field)) {
            return this.value[this.column.field]
          }
        }
        return this.value
      }
      return null
    }
  },
  methods: {
    async handleRemove ({ index, model }) {
      // console.log('REMOVE', index)
      if (!model) {
        // console.log('no model', index)
        return
      }
      const title = relationLabel(model, this.titleColumn, this.relationParentColumnMap[this.titleColumn.field])
      try {
        await this.$q.dialog({
          title: 'Confirm Detach',
          message: `Are you sure you want to detach ${title}?`,
          ok: 'Detach',
          cancel: 'Cancel'
        })
        this.$emit('manual-update', this.column.field, null)
        // this.$emit('manual-update', this.column.relation.localKey, null)
        // console.log('detach column', this.column)
      } catch (e) {
        // console.log(e.message)
      }
    },
    async handleSelectClick ({ index, model }) {
      // console.log('clicked', index, model)
      this.selected(model)
    }
  }
}
</script>
